/** Gotowość do sprzedaży jako JEDNO zdanie, nie sześć plakietek obok dwóch
 * przycisków (audyt designu 2026-10-06, handoff „Light redesign" §2a–2c).
 *
 * Moduł SKŁADA stan z istniejących źródeł prawdy (`policy.ts`, `sync.ts`,
 * `api.ts`) — nie dubluje żadnej reguły, tylko je odczytuje i nazywa jednym
 * z trzech stanów (`ok`/`warn`/`bad`), z tytułem, podtytułem i jednym CTA. */

import { kv } from "./db";
import { Bundle } from "./engine";
import * as api from "./api";
import * as vault from "./vault";
import { bundleFreshness, poolCheck, rejectionNotice, Freshness, PoolCheck, Rejection } from "./policy";
import * as sale from "./sale";
import * as sync from "./sync";
import { SyncStageResult } from "./sync";

export type ReadinessState = "ok" | "warn" | "bad";

export interface ReadinessRow {
  state: ReadinessState;
  title: string;
  subtitle: string;
}

export interface Readiness {
  state: ReadinessState;
  title: string;
  subtitle: string;
  /** null = ekran poniżej (konfiguracja/logowanie/PIN) JEST już tą akcją —
   * powtórzenie jej w pasku byłoby pustym przyciskiem obok formularza. */
  cta: { label: string; run: () => void | Promise<void> } | null;
  rows: ReadinessRow[];
}

const STAGE_LABEL: Record<SyncStageResult["key"], string> = {
  transakcje: "Transakcje", pula: "Pula numerów", taryfa: "Taryfa",
};

function stageSummary(stages: SyncStageResult[]): string {
  if (!stages.length) return "jeszcze nie synchronizowano";
  const ok = stages.filter((s) => s.status === "ok").length;
  const nieudane = stages.filter((s) => s.status === "error").map((s) => STAGE_LABEL[s.key]);
  return `${ok} z ${stages.length} etapów udane`
    + (nieudane.length ? ` (bez powodzenia: ${nieudane.join(", ")})` : "");
}

/** Jeden odczyt gotowości — woła go pasek stanu I ekran sprzedaży, żeby oba
 * mówiły to samo (dawne `offlineReadiness()` w `main.ts`, teraz tutaj i z
 * jawnym werdyktem zamiast rozsypanych flag czytanych osobno w dwóch miejscach). */
export async function computeReadiness(onSync: () => void | Promise<void>): Promise<Readiness> {
  const now = new Date();
  const cfg = await api.config();

  // Etapy PRZED ekranem sprzedaży: pasek ma mówić PRAWDĘ, ale formularz poniżej
  // już JEST tą czynnością, więc CTA tu byłoby drugim przyciskiem do tego samego.
  if (!cfg?.apiKey || !cfg.deviceId)
    return { state: "bad", title: "Sprzedaż zablokowana",
      subtitle: "Urządzenie nie jest zarejestrowane — wypełnij formularz poniżej.",
      cta: null, rows: [] };
  const hasVault = await kv.get("vault");
  if (!cfg.conductor || !hasVault)
    return { state: "bad", title: "Sprzedaż zablokowana",
      subtitle: "Zaloguj się jako konduktor, aby ustalić PIN.", cta: null, rows: [] };
  if (!vault.isUnlocked())
    return { state: "bad", title: "Aplikacja zablokowana",
      subtitle: "Podaj PIN, aby odblokować.", cta: null, rows: [] };

  const bundle = await kv.get<Bundle>("bundle");
  const fresh: Freshness = bundleFreshness(bundle?.compiled_at, bundle?.offline_policy, now);
  const pool: PoolCheck = poolCheck(await sale.poolState(), now);
  const rejection: Rejection | null = await sync.lastRejection();
  const pending = await sync.pendingCount();
  const stages = await sync.lastSyncStages();
  const off = api.isOffline();

  const rows: ReadinessRow[] = [
    { state: !bundle ? "bad" : fresh.state === "blocked" ? "bad"
        : fresh.state === "warn" || fresh.state === "unknown" ? "warn" : "ok",
      title: "Taryfa",
      subtitle: fresh.reason ?? (bundle
        ? `skompilowana ${bundle.compiled_at.slice(0, 16).replace("T", " ")}` : "brak pakietu") },
    { state: pool.ok ? (pool.remaining < 10 ? "warn" : "ok") : "bad",
      title: "Pula numerów",
      subtitle: pool.reason ?? `zostało ${pool.remaining}` },
    { state: rejection ? "bad" : pending > 0 ? "warn" : "ok",
      title: "Transakcje",
      subtitle: rejection ? (rejectionNotice(rejection) ?? "paczka odrzucona")
        : pending > 0 ? `${pending} czeka na wysłanie` : "wszystko wysłane" },
    { state: stages.length && stages.some((s) => s.status === "error") ? "warn" : "ok",
      title: "Ostatnia synchronizacja", subtitle: stageSummary(stages) },
  ];

  // Brak taryfy / pusta pula to TWARDA blokada — bez sieci nic nie policzy ceny
  // ani nie wyda numeru, więc żaden argument „ale jedna transakcja czeka" tego
  // nie przeważa.
  if (!bundle || pool.remaining <= 0 || (off && fresh.state === "blocked")) {
    const powod = !bundle ? "brak pakietu taryfowego"
      : (off && fresh.state === "blocked") ? fresh.reason!
      : pool.reason ?? "pula numerów wyczerpana";
    return { state: "bad", title: "Sprzedaż zablokowana", subtitle: powod,
      cta: off ? null : { label: "Synchronizuj teraz", run: onSync }, rows };
  }

  // Ostatnia synchronizacja częściowa ALBO pula kończy się — sprzedaż DZIAŁA,
  // ale kasjer ma dostać sygnał, zanim to się zmieni w twardą blokadę.
  const czesciowa = stages.length > 0 && stages.some((s) => s.status === "error");
  if (czesciowa) {
    return { state: "warn", title: "Zsynchronizowano częściowo",
      subtitle: `${stageSummary(stages)} · możesz sprzedawać`,
      cta: off ? null : { label: "Synchronizuj ponownie", run: onSync }, rows };
  }
  if (pool.remaining < 10) {
    return { state: "warn", title: "Kończy się pula numerów",
      subtitle: `zostało ${pool.remaining} — zsynchronizuj, zanim się skończy`,
      cta: off ? null : { label: "Synchronizuj ponownie", run: onSync }, rows };
  }
  if (!off && fresh.state === "warn") {
    return { state: "warn", title: "Taryfa niedługo się przeterminuje",
      subtitle: fresh.reason!, cta: { label: "Synchronizuj ponownie", run: onSync }, rows };
  }
  if (rejection) {
    return { state: "warn", title: "Poprzednia paczka odrzucona",
      subtitle: rejectionNotice(rejection) ?? "", cta: null, rows };
  }

  return { state: "ok", title: off ? "Możesz sprzedawać offline" : "Możesz sprzedawać online",
    subtitle: pending > 0 ? `${pending} transakcji czeka na wysłanie` : "Wszystko wysłane",
    cta: null, rows };
}
